import { formatExportedAt } from '@/pocs/chat/export/transcript'
import { Marked } from 'marked'
import { getSanitizedHtml } from '@deepcloud/deep-ui-utils/sanitize'
import type { ChatTranscript } from '@/pocs/chat/export/types'

const markdown = new Marked({ async: false })

// the shared sanitizer drops these by default, but exported answers need them
const SANITIZE_CONFIG = { ADD_TAGS: ['pre', 'code', 'th', 'del'] }

const STYLES = `
  body { font-family: system-ui, sans-serif; line-height: 1.5; max-width: 800px; margin: 2rem auto; padding: 0 1rem; color: #1f1f1f; }
  header { border-bottom: 1px solid #e0e0e0; margin-bottom: 1.5rem; }
  .meta { color: #6b6b6b; font-size: 0.875rem; }
  .question { background: #f5f5f5; border-radius: 8px; padding: 0.75rem 1rem; white-space: pre-wrap; }
  section { margin-bottom: 2rem; }
  pre { background: #f5f5f5; padding: 0.75rem; overflow-x: auto; }
  table { border-collapse: collapse; } td, th { border: 1px solid #e0e0e0; padding: 0.25rem 0.5rem; }
`

function escapeHtml(text: string) {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

export function toHtml(transcript: ChatTranscript): string {
  const { title, locale, contextFolders, exchanges, labels } = transcript
  const sections = exchanges
    .map(
      ({ question, answer, isComplete }) => `
    <section>
      <h2>${escapeHtml(labels.question)}</h2>
      <div class="question">${escapeHtml(question)}</div>
      <h2>${escapeHtml(labels.answer)}</h2>
      <div class="answer">${getSanitizedHtml(markdown.parse(answer) as string, SANITIZE_CONFIG)}</div>
      ${isComplete ? '' : `<p class="meta">${escapeHtml(labels.incompleteAnswer)}</p>`}
    </section>`,
    )
    .join('')

  return `<!doctype html>
<html lang="${escapeHtml(locale)}">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>${STYLES}</style>
  </head>
  <body>
    <header>
      <h1>${escapeHtml(title)}</h1>
      <p class="meta">${escapeHtml(labels.context)}: ${escapeHtml(contextFolders.join(', '))}</p>
      <p class="meta">${escapeHtml(labels.generatedBy)} · ${escapeHtml(formatExportedAt(transcript))}</p>
    </header>
    ${sections}
  </body>
</html>
`
}
